import { useState } from 'react';
import { CreditCard, Wallet, Banknote, ShieldAlert, ShieldCheck, Edit2, CheckCircle, X, RotateCcw } from 'lucide-react';
import { formatDateTime } from 'utils/dateFormatter';
import { getVerificationStatus } from '../utils';
import { useUpdateSellerCodSettings } from 'features/seller/hooks/useUpdateSellerCodSettings';
import { useGlobalConfig } from 'features/globalConfiguration/hooks/useGlobalConfig';

const FinancialTab = ({ seller, onOpenBankModal }) => {
  const diagnostics = seller?.razorpayDiagnostics;
  const requirements = Array.isArray(diagnostics?.requirements) ? diagnostics.requirements : [];

  const { data: globalConfigData } = useGlobalConfig();
  const globalCodLimit = globalConfigData?.data?.cod_max_due_limit ?? 80;
  const isCustomLimit = seller?.financial?.codDueLimit != null;

  const { mutate: updateCodSettings, isPending: isUpdating } = useUpdateSellerCodSettings();

  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [newDueLimit, setNewDueLimit] = useState(String(seller?.financial?.codDueLimit ?? globalCodLimit));

  const [isClearDuesModalOpen, setIsClearDuesModalOpen] = useState(false);
  const [deductAmount, setDeductAmount] = useState(String(seller?.financial?.codDueAmount || 0));

  const codDueAmount = seller?.financial?.codDueAmount || 0;
  const codDueLimit = isCustomLimit ? seller.financial.codDueLimit : globalCodLimit;
  const isBlocked = !!seller?.financial?.isCodBlocked || (codDueAmount >= codDueLimit);
  const remainingLimit = Math.max(0, codDueLimit - codDueAmount);

  const handleSaveLimit = () => {
    const limitNum = Number(newDueLimit);
    if (isNaN(limitNum) || limitNum < 0) return;
    updateCodSettings(
      { sellerId: seller._id, codDueLimit: limitNum },
      { onSuccess: () => setIsLimitModalOpen(false) }
    );
  };

  const handleResetToGlobal = () => {
    updateCodSettings(
      { sellerId: seller._id, codDueLimit: null },
      { onSuccess: () => setIsLimitModalOpen(false) }
    );
  };

  const handleClearAllDues = () => {
    updateCodSettings(
      { sellerId: seller._id, clearDues: true },
      { onSuccess: () => setIsClearDuesModalOpen(false) }
    );
  };

  const handleDeductAmount = () => {
    const amountNum = Number(deductAmount);
    if (!amountNum || amountNum <= 0) return;
    updateCodSettings(
      { sellerId: seller._id, dueAmountToDeduct: amountNum },
      { onSuccess: () => setIsClearDuesModalOpen(false) }
    );
  };

  const handleToggleBlock = () => {
    updateCodSettings({
      sellerId: seller._id,
      isCodBlocked: !seller?.financial?.isCodBlocked,
    });
  };

  const prettifyStatus = (value) => {
    if (!value) return 'N/A';
    return String(value).replace(/_/g, ' ');
  };

  return (
    <>
      {/* Bank Details */}
      <div className='bg-card border border-border rounded-lg p-6'>
        <div className='flex items-center justify-between mb-4'>
          <h2 className='text-lg font-semibold flex items-center'>
            <CreditCard className='mr-2' size={20} />
            Bank Details
          </h2>
          {getVerificationStatus(seller?.bankDetails?.isVerified)}
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          <div>
            <label className='text-xs text-muted-foreground'>Account Holder Name</label>
            <p className='text-sm font-medium mt-1'>{seller?.bankDetails?.accountHolderName || 'N/A'}</p>
          </div>
          <div>
            <label className='text-xs text-muted-foreground'>Account Number</label>
            <p className='text-sm font-medium mt-1'>{seller?.bankDetails?.accountNumber || 'N/A'}</p>
          </div>
          <div>
            <label className='text-xs text-muted-foreground'>IFSC Code</label>
            <p className='text-sm font-medium mt-1'>{seller?.bankDetails?.ifscCode || 'N/A'}</p>
          </div>
          <div>
            <label className='text-xs text-muted-foreground'>Bank Name</label>
            <p className='text-sm font-medium mt-1'>{seller?.bankDetails?.bankName || 'N/A'}</p>
          </div>
          {/* <div>
            <label className='text-xs text-muted-foreground'>Branch Name</label>
            <p className='text-sm font-medium mt-1'>{seller?.bankDetails?.branchName || 'N/A'}</p>
          </div> */}
          <div>
            <label className='text-xs text-muted-foreground'>UPI ID</label>
            <p className='text-sm font-medium mt-1'>{seller?.bankDetails?.upiId || 'N/A'}</p>
          </div>
          {seller?.bankDetails?.verifiedAt && (
            <div>
              <label className='text-xs text-muted-foreground'>Verified At</label>
              <p className='text-sm font-medium mt-1'>{formatDateTime(seller.bankDetails.verifiedAt)}</p>
            </div>
          )}
        </div>
        {!seller?.bankDetails?.isVerified && (
          <div className='mt-4'>
            <button
              onClick={onOpenBankModal}
              className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors'
            >
              Verify Bank Details
            </button>
          </div>
        )}

        {diagnostics && (
          <div className='mt-6 border-t border-border pt-4'>
            <h3 className='text-sm font-semibold text-foreground mb-3'>Razorpay Diagnostics</h3>

            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4'>
              <div className='rounded-md border border-border bg-muted/30 p-3'>
                <p className='text-[11px] text-muted-foreground uppercase tracking-wide'>Activation Status</p>
                <p className='text-sm font-medium mt-1 capitalize'>{prettifyStatus(diagnostics?.activation_status)}</p>
              </div>
              <div className='rounded-md border border-border bg-muted/30 p-3'>
                <p className='text-[11px] text-muted-foreground uppercase tracking-wide'>Account Status</p>
                <p className='text-sm font-medium mt-1 capitalize'>{prettifyStatus(diagnostics?.account_status)}</p>
              </div>
              <div className='rounded-md border border-border bg-muted/30 p-3'>
                <p className='text-[11px] text-muted-foreground uppercase tracking-wide'>Account ID</p>
                <p className='text-sm font-medium mt-1 break-all'>{diagnostics?.accountId || 'N/A'}</p>
              </div>
              <div className='rounded-md border border-border bg-muted/30 p-3'>
                <p className='text-[11px] text-muted-foreground uppercase tracking-wide'>Product ID</p>
                <p className='text-sm font-medium mt-1 break-all'>{diagnostics?.productId || 'N/A'}</p>
              </div>
            </div>

            <div>
  <p className='text-xs text-muted-foreground mb-2'>
    Errors From Razor Pay Verification
  </p>

  {(() => {
    const errorItems = [
      { label: 'Step 1 Error', value: diagnostics?.step1Error },
      { label: 'PAN Verification Error', value: diagnostics?.step2Error },
      { label: 'PAN Update Error', value: diagnostics?.step2UpdateError },
      { label: 'Account Activation Error', value: diagnostics?.step3Error },
      { label: 'Bank Verification Error', value: diagnostics?.step4Error },
      { label: 'General Error', value: diagnostics?.generalError },
    ].filter((item) => item.value && String(item.value).trim() !== '[object Object]');

    if (errorItems.length === 0) {
      return <p className='text-sm text-muted-foreground'>No errors found.</p>;
    }

    return (
      <div className='space-y-3'>
        {errorItems.map((item, index) => {
          // Razorpay locks the activation form's fields once the submission
          // is under their review — this isn't an actionable in-app error,
          // just a status to wait out, so surface it as informational instead.
          const isPendingReviewLock =
            typeof item.value === 'string' && /locked for editing/i.test(item.value);

          if (isPendingReviewLock) {
            return (
              <div key={index} className='rounded-md border border-blue-200 bg-blue-50 p-3'>
                <p className='text-[11px] text-blue-600 uppercase tracking-wide font-semibold'>
                  Pending Razorpay Review
                </p>
                <p className='text-sm mt-1 text-blue-700'>
                  Submitted to Razorpay and awaiting their review — this field is
                  temporarily locked from further edits. No action needed; it will
                  unlock once Razorpay completes review or requests clarification.
                </p>
              </div>
            );
          }

          return (
            <div key={index} className='rounded-md border border-red-200 bg-red-50 p-3'>
              <p className='text-[11px] text-red-600 uppercase tracking-wide font-semibold'>
                {item.label}
              </p>
              <p className='text-sm mt-1 text-red-700 break-all'>{String(item.value)}</p>
            </div>
          );
        })}
      </div>
    );
  })()}
</div>
          </div>
        )}
      </div>

      {/* Cash On Delivery (COD) & Commission Dues */}
      <div className='bg-card border border-border rounded-lg p-6'>
        <div className='flex flex-wrap items-center justify-between gap-3 mb-4'>
          <div>
            <h2 className='text-lg font-semibold flex items-center gap-2'>
              <Banknote className='text-primary' size={20} />
              Cash on Delivery (COD) & Commission Dues
            </h2>
            <p className='text-xs text-muted-foreground mt-0.5'>
              Manage the partner's cash collection dues, due limit, and COD blocking status.
            </p>
          </div>

          <div className='flex items-center gap-2'>
            {isBlocked ? (
              <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-destructive/10 text-destructive border border-destructive/20'>
                <ShieldAlert size={14} />
                COD Blocked (Limit Reached)
              </span>
            ) : (
              <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-600 border border-emerald-500/20'>
                <ShieldCheck size={14} />
                COD Active (Allowed)
              </span>
            )}
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
          <div className='bg-muted/30 border border-border rounded-lg p-4'>
            <label className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
              Outstanding COD Dues
            </label>
            <p className='text-2xl font-bold text-foreground mt-1'>
              ₹{(seller?.financial?.codDueAmount || 0).toLocaleString()}
            </p>
            <p className='text-xs text-muted-foreground mt-1'>
              Platform commission collected in cash by provider
            </p>
          </div>

          <div className='bg-muted/30 border border-border rounded-lg p-4'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <label className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                  COD Due Limit
                </label>
                {isCustomLimit ? (
                  <span className='px-1.5 py-0.5 text-[10px] font-semibold bg-purple-500/10 text-purple-600 rounded border border-purple-500/20'>
                    Custom Override
                  </span>
                ) : (
                  <span className='px-1.5 py-0.5 text-[10px] font-semibold bg-muted text-muted-foreground rounded border border-border'>
                    Global Default
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                  setNewDueLimit(String(seller?.financial?.codDueLimit ?? globalCodLimit));
                  setIsLimitModalOpen(true);
                }}
                className='text-xs text-primary hover:underline font-medium inline-flex items-center gap-1'
              >
                <Edit2 size={12} /> Edit Limit
              </button>
            </div>
            <p className='text-2xl font-bold text-foreground mt-1'>
              ₹{codDueLimit.toLocaleString()}
            </p>
            <p className='text-xs text-muted-foreground mt-1'>
              {isCustomLimit
                ? 'Partner has custom limit set'
                : `Inheriting global config limit (₹${globalCodLimit})`}
            </p>
          </div>

          <div className='bg-muted/30 border border-border rounded-lg p-4'>
            <label className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
              Remaining Credit
            </label>
            <p className={`text-2xl font-bold mt-1 ${remainingLimit <= 0 ? 'text-destructive' : 'text-emerald-600'}`}>
              ₹{remainingLimit.toLocaleString()}
            </p>
            <p className='text-xs text-muted-foreground mt-1'>
              Cash booking capacity before threshold
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className='flex flex-wrap items-center gap-3 pt-4 border-t border-border'>
          <button
            onClick={() => {
              setDeductAmount(String(seller?.financial?.codDueAmount || 0));
              setIsClearDuesModalOpen(true);
            }}
            disabled={(seller?.financial?.codDueAmount || 0) <= 0 || isUpdating}
            className='px-4 py-2 text-sm bg-primary text-primary-foreground font-medium rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2'
          >
            <CheckCircle size={15} />
            Record Cash Clearance / Clear Dues
          </button>

          <button
            onClick={() => {
              setNewDueLimit(String(seller?.financial?.codDueLimit ?? 80));
              setIsLimitModalOpen(true);
            }}
            disabled={isUpdating}
            className='px-4 py-2 text-sm border border-border text-foreground font-medium rounded-md hover:bg-muted transition-colors inline-flex items-center gap-2'
          >
            <Edit2 size={15} />
            Adjust COD Limit
          </button>

          <button
            onClick={handleToggleBlock}
            disabled={isUpdating}
            className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors inline-flex items-center gap-2 ${
              seller?.financial?.isCodBlocked
                ? 'border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10'
                : 'border-destructive/30 text-destructive hover:bg-destructive/10'
            }`}
          >
            {seller?.financial?.isCodBlocked ? (
              <>
                <ShieldCheck size={15} />
                Unblock COD Manually
              </>
            ) : (
              <>
                <ShieldAlert size={15} />
                Block COD Manually
              </>
            )}
          </button>
        </div>
      </div>

      {/* Financial Summary */}
      <div className='bg-card border border-border rounded-lg p-6'>
        <h2 className='text-lg font-semibold mb-4 flex items-center'>
          <Wallet className='mr-2' size={20} />
          Financial Earnings Summary
        </h2>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div className='bg-green-500/10 border border-green-500/20 rounded-lg p-4'>
            <label className='text-xs text-muted-foreground uppercase tracking-wide font-medium'>Total Earnings</label>
            <p className='text-2xl font-bold text-green-600 mt-1'>
              ₹{(seller?.financial?.totalEarnings || 0).toLocaleString()}
            </p>
          </div>
          <div className='bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4'>
            <label className='text-xs text-muted-foreground uppercase tracking-wide font-medium'>Pending Payouts</label>
            <p className='text-2xl font-bold text-yellow-600 mt-1'>
              ₹{(seller?.financial?.pendingPayouts || 0).toLocaleString()}
            </p>
          </div>
          <div className='bg-blue-500/10 border border-blue-500/20 rounded-lg p-4'>
            <label className='text-xs text-muted-foreground uppercase tracking-wide font-medium'>Completed Payouts</label>
            <p className='text-2xl font-bold text-blue-600 mt-1'>
              ₹{(seller?.financial?.completedPayouts || 0).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Modal: Adjust COD Limit */}
      {isLimitModalOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4'>
          <div className='bg-card border border-border rounded-lg shadow-xl w-full max-w-md p-6 space-y-4'>
            <div className='flex items-center justify-between border-b border-border pb-3'>
              <h3 className='text-lg font-semibold text-foreground flex items-center gap-2'>
                <Edit2 size={18} className='text-primary' />
                Adjust Provider COD Limit
              </h3>
              <button
                onClick={() => setIsLimitModalOpen(false)}
                className='text-muted-foreground hover:text-foreground'
              >
                <X size={18} />
              </button>
            </div>

            <p className='text-xs text-muted-foreground'>
              Set the maximum cash collection dues allowed for this specific partner. Leave blank or click "Use Global Default" to inherit the system default (₹{globalCodLimit}).
            </p>

            <div>
              <div className='flex justify-between items-center mb-1'>
                <label className='block text-sm text-foreground font-medium'>
                  Max Due Limit (₹)
                </label>
                <span className='text-xs text-muted-foreground'>
                  Global Default: ₹{globalCodLimit}
                </span>
              </div>
              <input
                type='number'
                min='0'
                value={newDueLimit}
                onChange={(e) => setNewDueLimit(e.target.value)}
                className='w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:ring-2 focus:ring-primary focus:border-transparent'
                placeholder={`Global default (${globalCodLimit})`}
              />
            </div>

            <div className='flex items-center justify-between gap-3 pt-3 border-t border-border'>
              {isCustomLimit ? (
                <button
                  type='button'
                  onClick={handleResetToGlobal}
                  disabled={isUpdating}
                  className='text-xs text-destructive hover:underline font-medium inline-flex items-center gap-1 disabled:opacity-50'
                >
                  <RotateCcw size={12} /> Reset to Global Default (₹{globalCodLimit})
                </button>
              ) : (
                <div />
              )}
              <div className='flex items-center gap-2'>
                <button
                  type='button'
                  onClick={() => setIsLimitModalOpen(false)}
                  className='px-3 py-1.5 border border-border rounded-md text-muted-foreground hover:text-foreground text-sm'
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={handleSaveLimit}
                  disabled={isUpdating || !newDueLimit}
                  className='px-3 py-1.5 bg-primary text-primary-foreground font-medium rounded-md hover:bg-primary/90 text-sm disabled:opacity-50'
                >
                  {isUpdating ? 'Saving...' : 'Save Limit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Clear / Deduct Dues */}
      {isClearDuesModalOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4'>
          <div className='bg-card border border-border rounded-lg shadow-xl w-full max-w-md p-6 space-y-4'>
            <div className='flex items-center justify-between border-b border-border pb-3'>
              <h3 className='text-lg font-semibold text-foreground flex items-center gap-2'>
                <CheckCircle size={18} className='text-primary' />
                Record Cash Clearance
              </h3>
              <button
                onClick={() => setIsClearDuesModalOpen(false)}
                className='text-muted-foreground hover:text-foreground'
              >
                <X size={18} />
              </button>
            </div>

            <p className='text-xs text-muted-foreground'>
              Record dues payment received offline in cash or bank transfer from this provider.
            </p>

            <div className='rounded-md bg-muted/40 p-3 text-xs space-y-1'>
              <div className='flex justify-between'>
                <span className='text-muted-foreground'>Current Outstanding:</span>
                <span className='font-semibold text-foreground'>₹{seller?.financial?.codDueAmount || 0}</span>
              </div>
            </div>

            <div>
              <label className='block text-sm text-foreground font-medium mb-1'>
                Amount Paid by Partner (₹)
              </label>
              <input
                type='number'
                min='1'
                max={seller?.financial?.codDueAmount || 0}
                value={deductAmount}
                onChange={(e) => setDeductAmount(e.target.value)}
                className='w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:ring-2 focus:ring-primary focus:border-transparent'
              />
            </div>

            <div className='flex justify-between items-center pt-3'>
              <button
                type='button'
                onClick={handleClearAllDues}
                disabled={isUpdating}
                className='text-xs text-primary underline hover:text-primary/80'
              >
                Clear Entire Balance
              </button>

              <div className='flex gap-2'>
                <button
                  type='button'
                  onClick={() => setIsClearDuesModalOpen(false)}
                  className='px-3 py-2 border border-border rounded-md text-muted-foreground hover:text-foreground text-sm'
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={handleDeductAmount}
                  disabled={isUpdating || !deductAmount || Number(deductAmount) <= 0}
                  className='px-4 py-2 bg-primary text-primary-foreground font-medium rounded-md hover:bg-primary/90 text-sm disabled:opacity-50'
                >
                  {isUpdating ? 'Recording...' : 'Record Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FinancialTab;

